import type { ReactElement } from "react"
import { useEffect } from "react"
import type { ErrorComponentProps } from "@tanstack/react-router"
import { useRouterState } from "@tanstack/react-router"

import { GenericErrorScreen } from "@/components/errors/generic-error-screen"
import { ReconnectingScreen } from "@/components/errors/reconnecting-screen"
import { isPrintPathname, isTransientRouteError } from "@/lib/route-error-utils"

/**
 * The headless PDF worker waits up to 90s for `body[data-print-ready]` ("true" | "error").
 * An error that escapes to the router boundary on a print route must set the attribute and
 * render a plain message — any retry/reload loop here would hang the export instead.
 * Mirrors print-token-page.tsx's own error effect.
 */
function PrintRouteErrorScreen(): ReactElement {
	useEffect(() => {
		document.body.dataset.printReady = "error"
		return () => {
			delete document.body.dataset.printReady
		}
	}, [])

	return (
		<div className="text-muted-foreground flex min-h-svh items-center justify-center p-10 text-center text-sm">
			We could not load this print view. Try again later.
		</div>
	)
}

/** Router `defaultErrorComponent`. NOTE: the `/print` branch must stay FIRST — auto-retry on a
 * print route would hang the PDF worker for its full timeout (see PrintRouteErrorScreen). */
export function RouterErrorComponent({ error }: ErrorComponentProps): ReactElement {
	const pathname = useRouterState({ select: (s) => s.location.pathname })

	if (isPrintPathname(pathname)) return <PrintRouteErrorScreen />
	if (isTransientRouteError(error)) return <ReconnectingScreen />
	return <GenericErrorScreen error={error} />
}
